import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { TransporterAPI } from '../../lib/api';
import { Loader2 } from 'lucide-react';

interface AssignTransporterFormProps {
  isOpen: boolean;
  onClose: () => void;
  onAssign: (transporterId: string, vehicleDetails?: any, notes?: string) => Promise<void>;
}

interface Transporter {
  _id: string;
  name: string;
  mobile: string;
  email: string;
  companyName?: string;
  vehicles?: Array<{
    _id: string;
    registrationNumber: string;
    type: string;
    capacity: {
      value: number;
      unit: string;
    };
  }>;
}

const AssignTransporterForm: React.FC<AssignTransporterFormProps> = ({
  isOpen,
  onClose,
  onAssign
}) => {
  const [transporterId, setTransporterId] = useState('');
  const [notes, setNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [transporters, setTransporters] = useState<Transporter[]>([]);
  const [selectedTransporter, setSelectedTransporter] = useState<Transporter | null>(null);
  const [loading, setLoading] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const [searchResults, setSearchResults] = useState<Transporter[]>([]);
  const [showTransporterDropdown, setShowTransporterDropdown] = useState(false);

  // Vehicle details
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [vehicleRegistration, setVehicleRegistration] = useState('');
  const [vehicleType, setVehicleType] = useState('');
  const [capacityValue, setCapacityValue] = useState('');
  const [capacityUnit, setCapacityUnit] = useState('ton');

  // Load transporters
  useEffect(() => {
    const fetchTransporters = async () => {
      setLoading(true);
      try {
        const response = await TransporterAPI.getAllTransporters({
          isActive: true,
          verificationStatus: 'verified'
        });
        setTransporters(response.data.transporters);
        setSearchResults(response.data.transporters.slice(0, 10)); // Initially show first 10 transporters
      } catch (error) {
        console.error('Failed to fetch transporters:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchTransporters();
    }
  }, [isOpen]);

  // Filter transporters based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults(transporters.slice(0, 10)); // Show first 10 transporters when no search query
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = transporters.filter(
        transporter =>
          transporter.name.toLowerCase().includes(query) ||
          (transporter.companyName && transporter.companyName.toLowerCase().includes(query))
      ).slice(0, 10); // Limit to 10 results
      setSearchResults(filtered);
    }
  }, [searchQuery, transporters]);

  // Update selected transporter when transporterId changes
  useEffect(() => {
    if (transporterId) {
      const transporter = transporters.find(t => t._id === transporterId) || null;
      setSelectedTransporter(transporter);

      // Reset vehicle selection
      setSelectedVehicleId('');
      setVehicleRegistration('');
      setVehicleType('');
      setCapacityValue('');
      setCapacityUnit('ton');
    } else {
      setSelectedTransporter(null);
    }
  }, [transporterId, transporters]);

  // Set vehicle details when a vehicle is selected
  useEffect(() => {
    if (selectedVehicleId && selectedTransporter?.vehicles) {
      const vehicle = selectedTransporter.vehicles.find(v => v._id === selectedVehicleId);
      if (vehicle) {
        setVehicleRegistration(vehicle.registrationNumber);
        setVehicleType(vehicle.type);
        setCapacityValue(vehicle.capacity.value.toString());
        setCapacityUnit(vehicle.capacity.unit);
      }
    }
  }, [selectedVehicleId, selectedTransporter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!transporterId || isAssigning) return;

    setIsAssigning(true);

    try {
      await onAssign(transporterId, undefined, notes);
    } catch (error) {
      console.error("Assignment failed in form:", error);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleTransporterSelect = (id: string) => {
    setTransporterId(id);
    const selected = transporters.find(t => t._id === id);
    setSearchQuery(selected ? (selected.companyName || selected.name) : '');
    setShowTransporterDropdown(false); // Hide dropdown after selection
  };

  const handleReset = () => {
    setTransporterId('');
    setNotes('');
    setSearchQuery('');
    setShowTransporterDropdown(false);
    setSelectedVehicleId('');
    setVehicleRegistration('');
    setVehicleType('');
    setCapacityValue('');
    setCapacityUnit('ton');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md w-full max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>Assign Order to Transporter</DialogTitle>
          <DialogDescription>
            Select a transporter to handle delivery
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="transporter">Transporter</Label>
              <div className="relative">
                <Input
                  id="transporter"
                  placeholder="Search for transporter..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowTransporterDropdown(true);
                  }}
                  onFocus={() => setShowTransporterDropdown(true)}
                />

                {showTransporterDropdown && searchResults.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-y-auto mobile-scrolling">
                    {searchResults.map((transporter) => (
                      <div
                        key={transporter._id}
                        className={`p-3 cursor-pointer hover:bg-gray-100 border-b last:border-0 ${transporterId === transporter._id ? 'bg-blue-50' : ''}`}
                        onClick={() => handleTransporterSelect(transporter._id)}
                      >
                        <div className="font-medium">{transporter.name}</div>
                        {transporter.companyName && (
                          <div className="text-sm text-gray-500">{transporter.companyName}</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {selectedTransporter && (
              <div className="rounded-md border bg-blue-50/60 p-3 space-y-1">
                <div className="text-sm font-semibold text-gray-900">
                  {selectedTransporter.companyName || selectedTransporter.name}
                </div>
                {selectedTransporter.companyName && (
                  <div className="text-xs text-gray-600">Contact: {selectedTransporter.name}</div>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                  {selectedTransporter.mobile && <span>📞 {selectedTransporter.mobile}</span>}
                  {selectedTransporter.email && <span>✉️ {selectedTransporter.email}</span>}
                </div>
                <div className="text-xs text-gray-600">
                  {selectedTransporter.vehicles?.length
                    ? `${selectedTransporter.vehicles.length} vehicle(s) available`
                    : 'No vehicles registered yet'}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add notes about this assignment..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <DialogFooter className="sticky bottom-0 bg-background pt-4 pb-0 mt-4 border-t">
              <div className="flex w-full gap-3">
                <Button type="button" variant="outline" onClick={handleReset} className="flex-1">
                  Reset
                </Button>
                <Button type="submit" disabled={!transporterId || isAssigning} className="flex-1">
                  {isAssigning ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Assigning...
                    </>
                  ) : (
                    'Assign Transporter'
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AssignTransporterForm; 